import React from "react";
import { MapPin, Loader2, X } from "lucide-react";

/**
 * Dansk adresse-autocomplete via DAWA (Danmarks Adresser Web API).
 * Free, public, no API key required — maintained by Klima-, Energi- og
 * Forsyningsministeriet / Erhvervsstyrelsen.
 *
 * Props:
 *   value         (string)               — full address text (controlled)
 *   onChange      (text => void)         — fires on every keystroke
 *   onSelect      ({address,zip,city}=>) — fires when user picks a suggestion
 *   placeholder   (string)
 *   testId        (string)
 *   icon          (boolean)              — show MapPin icon prefix
 *   className     (string)
 *   inputClassName (string)
 *   limit         (number, default 6)
 *   includeAccess (bool)  — use /adgangsadresser/autocomplete instead of /adresser
 *                            (omits floor/door — useful for street-level only)
 */
export default function AddressAutocomplete({
  value,
  onChange,
  onSelect,
  placeholder = "fx Storgade 5, 4000 Roskilde",
  testId = "address-autocomplete",
  icon = false,
  className = "",
  inputClassName = "",
  limit = 6,
  includeAccess = false,
  autoFocus = false,
}) {
  const [suggestions, setSuggestions] = React.useState([]);
  const [open, setOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [active, setActive] = React.useState(-1);
  const wrapperRef = React.useRef(null);
  const abortRef = React.useRef(null);

  // Close on outside click
  React.useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Debounced DAWA fetch
  React.useEffect(() => {
    if (!value || value.trim().length < 2) {
      setSuggestions([]); setLoading(false);
      return;
    }
    setLoading(true);
    const path = includeAccess ? "adgangsadresser" : "adresser";
    const url = `https://api.dataforsyningen.dk/${path}/autocomplete?q=${encodeURIComponent(value)}&per_side=${limit}`;
    const ctrl = new AbortController();
    if (abortRef.current) abortRef.current.abort();
    abortRef.current = ctrl;
    const timer = setTimeout(async () => {
      try {
        const r = await fetch(url, { signal: ctrl.signal });
        if (!r.ok) throw new Error(`DAWA ${r.status}`);
        const data = await r.json();
        setSuggestions(Array.isArray(data) ? data : []);
        setActive(-1);
      } catch (e) {
        if (e.name !== "AbortError") {
          console.warn("DAWA autocomplete failed:", e);
          setSuggestions([]);
        }
      } finally {
        setLoading(false);
      }
    }, 220);
    return () => { clearTimeout(timer); ctrl.abort(); };
  }, [value, limit, includeAccess]);

  const pick = (s) => {
    const a = s.adresse || s.adgangsadresse || {};
    const street = a.vejnavn || a.adresseringsvejnavn || "";
    const housenr = a.husnr || "";
    let line = `${street} ${housenr}`.trim();
    if (a.etage || a.dør) {
      const dim = [a.etage, a.dør].filter(Boolean).join(". ");
      if (dim) line += `, ${dim}`;
    }
    const zip = a.postnr || a.stormodtagerpostnr || "";
    const city = a.postnrnavn || a.stormodtagerpostnrnavn || "";
    onChange(s.tekst || `${line}, ${zip} ${city}`);
    onSelect?.({ address: line, zip, city });
    setOpen(false);
    setSuggestions([]);
  };

  const onKey = (e) => {
    if (!open || suggestions.length === 0) {
      if (e.key === "ArrowDown" && suggestions.length) setOpen(true);
      return;
    }
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, suggestions.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === "Enter") {
      if (active >= 0 && active < suggestions.length) { e.preventDefault(); pick(suggestions[active]); }
    } else if (e.key === "Escape") { setOpen(false); }
  };

  return (
    <div ref={wrapperRef} className={`relative ${className}`}>
      <div className="relative">
        {icon && <MapPin size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-red pointer-events-none" />}
        <input
          type="text"
          className={`input-flat ${icon ? "pl-9" : ""} ${inputClassName}`}
          placeholder={placeholder}
          value={value || ""}
          onChange={(e) => { onChange(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKey}
          autoComplete="off"
          autoFocus={autoFocus}
          data-testid={testId}
          aria-autocomplete="list"
          aria-controls={`${testId}-list`}
          aria-expanded={open && suggestions.length > 0}
        />
        {loading && (
          <Loader2 className="animate-spin absolute right-3 top-1/2 -translate-y-1/2 text-brand-text2" size={14} />
        )}
        {!loading && value && (
          <button
            type="button"
            onClick={() => { onChange(""); setSuggestions([]); }}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-brand-text2/70 hover:text-brand-dark p-1"
            aria-label="Ryd"
            data-testid={`${testId}-clear`}
          >
            <X size={14} />
          </button>
        )}
      </div>

      {open && suggestions.length > 0 && (
        <ul
          id={`${testId}-list`}
          role="listbox"
          className="absolute left-0 right-0 top-full mt-1 z-50 bg-white border border-brand-dark max-h-72 overflow-auto shadow-lg"
          data-testid={`${testId}-suggestions`}
        >
          {suggestions.map((s, i) => (
            <li
              key={s.tekst + i}
              role="option"
              aria-selected={i === active}
              onMouseDown={(e) => { e.preventDefault(); pick(s); }}
              onMouseEnter={() => setActive(i)}
              className={`px-3 py-2 text-sm cursor-pointer border-b border-brand last:border-0 flex items-center gap-2 ${i === active ? "bg-brand-bg2 text-brand-dark" : "text-brand-dark hover:bg-brand-bg2"}`}
            >
              <MapPin size={12} className="text-brand-red flex-shrink-0" />
              <span className="truncate">{s.tekst}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
